import React, { useState, useEffect, useRef, useCallback } from "react";
import { productApi } from "../services/api/productApi";
import { categoryApi } from "../services/api/categoryApi";
import { ICategory } from "../types";
import { wishlistApi } from "../services/api/wishlistApi";
import { cartApi } from "../services/api/cartApi";
import { ordersApi } from "../services/api/orderApi";
import { showSuccess, showError } from "../lib/toast";
import { notifyWishlistUpdate } from "../utils/wishlistUtils";
import { notifyCartUpdate } from "../utils/cartUpdateUtils";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import ImageWithLoader from "../components/ImageWithLoader";
import AnimatedSection from "../components/AnimatedSection";
import ParallaxSection from "../components/ParallaxSection";
import HoverCard from "../components/HoverCard";
import { motion } from "framer-motion";

const Home: React.FC = () => {
  const { isLoggedIn, user, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [wishlistLoading, setWishlistLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [ownedProducts, setOwnedProducts] = useState<Set<string>>(new Set());
  const [mostSellingProducts, setMostSellingProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);
  const mostSellingCarouselRef = useRef<any>(null);
  
  // Hero Banner Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  const bannerImages = [
    "/assets/images/banner-bg.jpg",
    "/assets/images/banner-bg_1.jpg", // Current image
    "/assets/images/banner-bg_2.png",
    "/assets/images/banner-bg_3.png", 
    "/assets/images/banner-bg_4.jpg", 
    "/assets/images/banner-bg_5.png", 
  ];
  
  const bannerTexts = [
    "The Ultimate Digital Marketplace, curated for modern creatives.",
    "The Complete Digital Platform, crafted for creative minds.",
    "A Premium Creative Marketplace, designed for the digital era.",
    "The Modern Creative Marketplace, built for bold creators.",
    "A Curated Digital Platform, made for today's creators.",
    "A Next-Generation Creative Marketplace, empowering digital innovators.",
  ];

  const fetchWishlist = useCallback(async () => {
    // Only fetch wishlist if user is logged in
    if (!isLoggedIn) {
      setWishlist([]);
      return;
    }
    
    try {
      const wishlistResponse: any = await wishlistApi.getWishlist();
      setWishlist(
        wishlistResponse?.items?.map((item: any) => item?.product?._id) || [],
      );
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      setWishlist([]);
    }
  }, [isLoggedIn]);

  const checkOwnedProducts = useCallback(async (productIds: string[]) => {
    if (!isLoggedIn || productIds.length === 0) return;
    
    try {
      const response: any = await ordersApi.checkMultipleProductsOwnership(productIds);
      if (response.success && response.data?.ownershipMap) {
        const owned = new Set<string>();
        Object.entries(response.data.ownershipMap).forEach(([productId, isOwned]) => {
          if (isOwned) owned.add(productId);
        });
        setOwnedProducts(owned);
      }
    } catch (error) {
      console.error("Error checking owned products:", error);
    }
  }, [isLoggedIn]);
  console.log("wishlist", wishlist);
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;

      const fetchData = async () => {
        try {
          // Fetch wishlist if user is logged in
          await fetchWishlist();

          // Featured products
          const featuredResponse = await productApi.getProducts();
          const featured = featuredResponse.data.products;
          console.log(featured, "#####################");
          
          setFeaturedProducts(featured);

          // Most selling products
          const mostSellingResponse =
            await productApi.getMostSellingProducts();
          const mostSelling = mostSellingResponse.data || [];
          setMostSellingProducts(mostSelling);

          // Fetch categories
          const categoriesResponse = await categoryApi.getCategories();
          console.log("categoriesResponse",categoriesResponse)
          setCategories(categoriesResponse.data);

          // Check ownership for all products
          if (isLoggedIn) {
            const allProductIds = [
              ...featured.map((p: any) => p._id),
              ...mostSelling.map((p: any) => p._id),
            ];
            // Remove duplicates
            const uniqueProductIds = Array.from(new Set(allProductIds));
            await checkOwnedProducts(uniqueProductIds);
          }
        } catch (err) {
          console.error("Error:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [fetchWishlist, checkOwnedProducts, isLoggedIn]); // Add checkOwnedProducts and isLoggedIn to dependency array
  console.log("featuredProducts", featuredProducts, "categories", categories);

  const goToPreviousSlide = () => {
    if (mostSellingCarouselRef.current) {
      mostSellingCarouselRef.current.previous();
    }
  };

  const goToNextSlide = () => {
    if (mostSellingCarouselRef.current) {
      mostSellingCarouselRef.current.next();
    }
  };

  // Check if a product is in the wishlist
  const isProductInWishlist = (productId: string) => {
    return wishlist.some((item) => item === productId);
  };

  // Refresh wishlist when auth state changes
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Auto-slide banner images
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(slideInterval);
  }, [bannerImages.length]);
  const convertInchesToCm = (size: string): string => {
    // Example: "7.9 x 5.3 inches"
    const numbers = size.replace("inches", "").trim().split("x");
    if (numbers.length !== 2) return size; // fallback

    const widthInCm = (parseFloat(numbers[0].trim()) * 2.54).toFixed(1);
    const heightInCm = (parseFloat(numbers[1].trim()) * 2.54).toFixed(1);

    return `${widthInCm} x ${heightInCm} cm`;
  };
  // Handle wishlist toggle
  const handleWishlistClick = async (productId: string) => {
    if (!isLoggedIn) {
      showError("Please login to add items to wishlist");
      navigate("/login");
      return;
    }
    
    if (wishlistLoading[productId]) return; // Prevent multiple clicks

    setWishlistLoading((prev) => ({ ...prev, [productId]: true }));

    try {
      const isInWishlist = isProductInWishlist(productId);
      if (isInWishlist) {
        await wishlistApi.removeFromWishlist(productId);
        setWishlist((prev) => prev.filter((item) => item !== productId));
        showSuccess("Removed from wishlist");
        notifyWishlistUpdate(); // Notify wishlist drawer to refresh
      } else {
        await wishlistApi.addToWishlist(productId);
        setWishlist((prev) => [...prev, productId]);
        showSuccess("Added to wishlist");
        notifyWishlistUpdate(); // Notify wishlist drawer to refresh
      }
    } catch (error: any) {
      console.error("Wishlist update error:", error);
      showError(error.message || "Failed to update wishlist");
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };

  // Handle add to cart
  const handleAddToCart = async (productId: string) => {
    if (!isLoggedIn) {
      showError("Please login to add items to cart");
      navigate("/login");
      return;
    }

    // Check if user already owns this product
    if (ownedProducts.has(productId)) {
      showError("You already own this product! Check your previous purchases.");
      navigate("/previous-purchase");
      return;
    }

    if (addingToCart === productId) return; // Prevent multiple clicks

    try {
      setAddingToCart(productId);

      await cartApi.addToCart({
        productId,
        quantity: 1,
      });

      showSuccess("Product added to cart successfully!");
      notifyCartUpdate(); // Notify other components about cart update
    } catch (error: any) {
      console.error("Error adding to cart:", error);

      if (error?.response?.status === 401) {
        showError("Please login to add items to cart");
        navigate("/login");
      } else if (error?.response?.data?.message) {
        // Check if it's an ownership error
        if (error.response.data.message.includes("already own")) {
          showError(error.response.data.message);
          setTimeout(() => navigate("/previous-purchase"), 1500);
        } else {
          showError(error.response.data.message);
        }
      } else {
        showError("Failed to add product to cart. Please try again.");
      }
    } finally {
      setAddingToCart(null);
    }
  };

  // Handle buy now
  const handleBuyNow = async (productId: string) => {
    if (!isLoggedIn) {
      showError("Please login to purchase");
      navigate("/login");
      return;
    }

    if (addingToCart === productId) return; // Prevent multiple clicks

    try {
      setAddingToCart(productId);

      // Add product to cart
      await cartApi.addToCart({ productId, quantity: 1 });
      
      // Navigate to checkout for immediate purchase
      navigate("/cart", { 
        state: { 
          isDirectBuy: true, 
          productId: productId 
        } 
      });
    } catch (error: any) {
      console.error("Error during buy now:", error);

      if (error?.response?.status === 401) {
        showError("Please login to purchase");
        navigate("/login");
      } else if (error?.response?.data?.message) {
        showError(error.response.data.message);
      } else {
        showError("Failed to process purchase. Please try again.");
      }
    } finally {
      setAddingToCart(null);
    }
  };
  return (
    <div>
      {/* Hero Banner Section */}
      <ParallaxSection speed={0.3}>
        <section className="hero_banner_sec">
          <div className="hero_sec">
            {/* Background Image Slider */}
            <div className="hero_slider_container">
              {bannerImages.map((image, index) => (
                <div
                  key={index}
                  className={`hero_slider_slide ${index === currentSlide ? 'active' : ''}`}
                  style={{ backgroundImage: `url(${image})` }}
                />
              ))}
            </div>
            
            {/* Slider Indicators */}
            <div className="hero_slider_indicators">
              {bannerImages.map((_, index) => (
                <button
                  key={index}
                  className={`hero_slider_indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <div className="container">
              <AnimatedSection direction="fade" delay={0.2}>
                <div className="hero_content d-flex flex-column align-items-sm-start align-items-center text-sm-start text-center">
                  <motion.h1
                    key={currentSlide}
                    className="heading_text_1 color_lightwhite fw_500"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    {(() => {
                      const text = bannerTexts[currentSlide];
                      const parts = text.split(',');
                      const mainText = parts[0];
                      const highlightText = parts.slice(1).join(',').trim();
                      return (
                        <>
                          {mainText},
                          <span className="color_creme fw_700">
                            {" "}
                            {highlightText}
                          </span>
                        </>
                      );
                    })()}
                  </motion.h1>
                  <motion.div
                    className="banner_btn_area"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    <a href="/products?collection=Canvas" className="banner_btn arrow_btn_two">
                      <span className="btn_text">Explore More</span>
                      <span className="btn_icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M17.9998 6H18.7498V5.25H17.9998V6ZM17.9998 6L17.4695 5.46967L5.46967 17.4697L6 18L6.53033 18.5303L18.5301 6.53033L17.9998 6ZM10.9999 6V6.75H17.9998V6V5.25H10.9999V6ZM17.9998 6H17.2498V12.9999H17.9998H18.7498V6H17.9998Z"
                            fill="#E7D5AD"
                          />
                        </svg>
                      </span>
                    </a>
                  </motion.div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      </ParallaxSection>

      {/* Shop Category Section */}
      <section id="shop_category" className="shop_category_section">
        <div className="shop_category_sec">
          <div className="container">
            <div className="shop_category_area">
              <div className="row">
                <div className="col-12">
                  <AnimatedSection direction="up" delay={0.1}>
                    <div className="shop_category_title_area common_title_area text-sm-center">
                      <h2 className="heading_text_2 mb-1">Explore Our Collections</h2>
                      <p className="font_two">
                        Find prints, instant downloads, and limited editions
                        organized for quick, effortless browsing.
                      </p>
                    </div>
                  </AnimatedSection>
                </div>
                <div className="col-12">
                  {loading ? (
                    <div className="text-center py-4 h-50">
                      <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 font_two">Loading...</p>
                    </div>
                  ) : (
                    <ul className="shop_category_list d-flex flex-lg-row flex-column">
                      {categories?.map((category, index) => {
                        console.log("category",category)
                        const categoryName = category.name.split(" ")[0];

                          const handleNavigate = async (categoryName: string) => {
                              // If accessing DigiVision, refresh user data to get latest KYC status
                              if (categoryName === "DigiVision" && isLoggedIn) {
                                try {
                                  await refreshUserData();
                                } catch (error) {
                                  console.error('Error refreshing user data:', error);
                                }
                              }
                              window.scrollTo(0, 0); // 👈 scrolls to top instantly
                              navigate(`/products?collection=${categoryName}`);
                          };

                        return (
                          <li
                            key={category._id}
                            className="shop_category_item position-relative overflow-hidden"
                          >
                            <HoverCard>
                              <button
                                type="button"
                                onClick={async () => {
                                if (
                                  categoryName === "DigiVision" &&
                                  !isLoggedIn
                                ) {
                                  showError(
                                    "Please login to view this category",
                                  );
                                  navigate("/login?isDigivision=true");
                                  return;
                                }

                                // Check if user is logged in but CosmoSlots KYC verification is not complete
                                if (
                                  categoryName === "DigiVision" &&
                                  isLoggedIn
                                ) {
                                  // Refresh user data to get latest KYC status
                                  try {
                                    await refreshUserData();
                                    // Get updated user data after refresh
                                    const updatedUserStr = localStorage.getItem('user');
                                    const updatedUser = updatedUserStr ? JSON.parse(updatedUserStr) : null;
                                    
                                    if (!updatedUser || !updatedUser.kycVerified) {
                                      showError(
                                        "Please wait for your KYC verification to complete before accessing this category",
                                      );
                                      return;
                                    }
                                  } catch (error) {
                                    console.error('Error refreshing user data:', error);
                                    // If refresh fails, check with current user data
                                    if (user && !user.kycVerified) {
                                      showError(
                                        "Please wait for your KYC verification to complete before accessing this category",
                                      );
                                      return;
                                    }
                                  }
                                }

                                  handleNavigate(categoryName);

                              }}
                              style={{ background: 'none', border: 'none', padding: 0, width: '100%' }}
                            >
                              <div className="shop_category_img">
                                {/* You can add a placeholder image or dynamic image logic here */}
                                <img
                                  src={`/assets/images/category_img_${index + 1}.png`}
                                  alt={category.name}
                                />
                              </div>
                              <div className="shop_category_info_box color_lightwhite d-flex flex-column font_two">
                                <h3 className="shop_category_heading text_20 lineheight_normal fw_700">
                                  {category.name}
                                  <span className="btn_icon info_icon">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <path
                                        d="M17.9998 6H18.7498V5.25H17.9998V6ZM17.9998 6L17.4695 5.46967L5.46967 17.4697L6 18L6.53033 18.5303L18.5301 6.53033L17.9998 6ZM10.9999 6V6.75H17.9998V6V5.25H10.9999V6ZM17.9998 6H17.2498V12.9999H17.9998H18.7498V6H17.9998Z"
                                        fill="#E7D5AD"
                                      />
                                    </svg>
                                  </span>
                                </h3>
                                <p className="d-sm-block d-none" style={{    textAlign: 'left'}}>
                                  {category?.description}
                                </p>
                              </div>
                            </button>
                            </HoverCard>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Most Selling Art Section */}
      <section id="most_selling" className="most_selling_art_section">
        <div className="most_selling_art_sec">
          <div className="container">
            <div className="most_selling_art_area">
              <div className="row">
                <div className="col-12">
                  <div className="most_selling_art_title_area common_title_area d-flex justify-content-between align-items-center">
                    <div className="sec_title_box">
                      <h2 className="heading_text_2 mb-1">Most Selling Art</h2>
                      <p className="font_two">
                        Hand‑selected highlights from our newest drops, trending
                        artworks, and staff favorites—updated weekly for fresh
                        inspiration.
                      </p>
                    </div>
                    <div className="title_arrow_box d-lg-flex d-none">
                      <button
                        onClick={goToPreviousSlide}
                        className="arrow-btn"
                        aria-label="Previous"
                      >
                        <img
                          src="/assets/images/nav_arrow_left.png"
                          alt="Previous"
                        />
                      </button>
                      <button
                        onClick={goToNextSlide}
                        className="arrow-btn"
                        aria-label="Next"
                      >
                        <img
                          src="/assets/images/nav_arrow_right.png"
                          alt="Next"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  {loading ? (
                    <div className="text-center py-4 h-50">
                      <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 font_two">Loading...</p>
                    </div>
                  ) : (
                    <Carousel
                      ref={mostSellingCarouselRef}
                      additionalTransfrom={0}
                      arrows={false}
                      autoPlay={true}
                      autoPlaySpeed={4000}
                      infinite
                      responsive={{
                        desktop: {
                          breakpoint: { max: 3000, min: 1024 },
                          items: 3,
                          slidesToSlide: 1,
                        },
                        tablet: {
                          breakpoint: { max: 1024, min: 464 },
                          items: 2,
                          slidesToSlide: 1,
                        },
                        mobile: {
                          breakpoint: { max: 464, min: 0 },
                          items: 1,
                          slidesToSlide: 1,
                        },
                      }}
                      showDots={false}
                      keyBoardControl
                      customTransition="transform 500ms ease-in-out"
                      transitionDuration={500}
                      containerClass="most_selling_art_carousel"
                      itemClass="most_selling_art_item"
                    >
                      {(mostSellingProducts || []).map((product) => (
                        <div
                          key={product._id}
                          className="most_selling_art_box d-flex flex-column"
                        >
                          <div className="most_selling_art_box_img position-relative">
                            {/* {isLoggedIn && ( */}
                              <button
                                type="button"
                                className="like_icon"
                                onClick={(e) => {
                                  if (isLoggedIn) {
                                    e.stopPropagation();
                                    handleWishlistClick(product?._id);
                                  } else {
                                    navigate("/login");
                                    return;
                                  }

                                }}
                                style={{ background: 'none', border: 'none', padding: 0, zIndex:1, }}
                              >
                                <svg
                                  width="46"
                                  height="46"
                                  viewBox="0 0 46 46"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    width="46"
                                    height="46"
                                    rx="23"
                                    fill="#FAF6EE"
                                  />
                                  <path
                                    d="M29.8569 14.7447C25.8652 12.6038 22.9991 15.7597 22.9991 15.7597C22.9991 15.7597 20.1328 12.6038 16.1411 14.7447C11.3064 17.3377 10.9592 27.492 22.9991 32C35.0389 27.492 34.6916 17.3377 29.8569 14.7447Z"
                                    stroke="#112C3B"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    fill={
                                      isProductInWishlist(product?._id)
                                        ? "#112C3B"
                                        : "none"
                                    }
                                    // fill={isWishlisted ? "#112C3B" : "none"} // ✅ fill if added
                                  />
                                </svg>
                              </button>
                            {/* )} */}
                            <ImageWithLoader
                              src={
                                product.image || "/assets/images/nav_img.png"
                              }
                              alt={product.name}
                              className="w-100"
                              style={{
                                width: "460px",
                                height: "250px",
                                objectFit: "cover",
                              }}
                            />
                            <div className="common_multi_btn_area">
                              {!ownedProducts.has(product._id) && (
                                <button
                                  type="button"
                                  className="blue_btn light_btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToCart(product._id);
                                  }}
                                >
                                  {addingToCart === product._id
                                    ? "Adding..."
                                    : "Add to Cart"}
                                </button>
                              )}
                              {ownedProducts.has(product._id) ? (
                                <button
                                  type="button"
                                  className="blue_btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/previous-purchase");
                                  }}
                                  style={{
                                    background: "#10B981",
                                    borderColor: "#10B981",
                                  }}
                                >
                                  ✓ Owned
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="blue_btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuyNow(product._id);
                                  }}
                                >
                                  {addingToCart === product._id
                                    ? "Processing..."
                                    : "Buy Now"}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="nav_inner_details_box_info font_two color_darkblue">
                            <p className="fw_500">{product.name}</p>
                            <p className="nav_inner_details_info fw_500">
                              {product?.artistId?.name},{" "}
                              {product?.artistId?.country}
                            </p>
                            <div className="nav_inner_details_price_box">
                              <span className="nav_box_price fw_700">
                                ${product.price.toFixed(2)}
                                {/*{product.isOnSale &&*/}
                                {/*  product.originalPrice > product.price && (*/}
                                {/*    <span className="original_price ml-2">*/}
                                {/*      ${product.originalPrice.toFixed(2)}*/}
                                {/*    </span>*/}
                                {/*  )}*/}
                              </span>
                              <span className="nav_box_cm fw_500">
                                {convertInchesToCm(
                                  product?.fileDetails.recommendedPrintSize,
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Art Gallery Section */}
      <section id="gallery_art" className="art_gallery_section">
        <div className="art_gallery_sec bg_darkblue color_lightwhite">
          <div className="container">
            <div className="art_gallery_area">
              <div className="row">
                <div className="col-12">
                  <div className="art_gallery_title_area common_title_area text-sm-center color_creme">
                    <h2 className="heading_text_2 mb-1">
                      See what's hot right now
                    </h2>
                    <p className="font_two">
                      Discover the most‑viewed, most‑saved, and fastest‑selling
                      artworks—refreshed throughout the day for real‑time buzz.
                    </p>
                  </div>
                </div>
                <div className="col-12">
                  <div className="art_gallery_list_area">
                    <div className="art_gallery_list_area_inner">
                      <div className="art_gallery_list_left">
                        <div
                          className="art_gallery_item position-relative overflow-hidden art_gallery_big_img"
                          id="art_gallery_item_2"
                        >
                          <img
                            src="/assets/images/art_gallery_img_1.png"
                            alt="art_gallery_img"
                          />
                          {/*<div className="art_gallery_detail_box font_two d-flex flex-column">*/}
                          {/*  <div className="art_gallery_detail_inner d-flex flex-column">*/}
                          {/*    <h3 className="text_20 lineheight_normal fw_700">*/}
                          {/*      Lorem Ipsum*/}
                          {/*    </h3>*/}
                          {/*    <p className="text_16 fw_500">*/}
                          {/*      Lorem Ipsum is simply dummy text of the printing*/}
                          {/*      and typesetting industry. Lorem Ipsum has been*/}
                          {/*      the industry's standard dummy text ever since*/}
                          {/*      the 1500s.*/}
                          {/*    </p>*/}
                          {/*  </div>*/}
                          {/*  <button type="button" className="white_btn">*/}
                          {/*    Buy Now*/}
                          {/*  </button>*/}
                          {/*</div>*/}
                        </div>
                      </div>
                      <div className="art_gallery_list_right">
                        <div className="art_gallery_list_upper">
                          <div
                            className="art_gallery_item position-relative overflow-hidden"
                            id="art_gallery_item_2"
                          >
                            <img
                              src="/assets/images/art_gallery_img_2.png"
                              alt="art_gallery_img"
                            />
                          </div>
                          <div
                            className="art_gallery_item position-relative overflow-hidden art_gallery_img_3"
                            id="art_gallery_item_3"
                          >
                            <img
                              src="/assets/images/art_gallery_img_3.png"
                              alt="art_gallery_img"
                            />
                          </div>
                        </div>
                        <div className="art_gallery_list_bottom">
                          <div
                            className="art_gallery_item position-relative overflow-hidden"
                            id="art_gallery_item_4"
                          >
                            <img
                              src="/assets/images/art_gallery_img_4.png"
                              alt="art_gallery_img"
                            />
                          </div>
                          <div
                            className="art_gallery_item position-relative overflow-hidden"
                            id="art_gallery_item_5"
                          >
                            <img
                              src="/assets/images/art_gallery_img_5.png"
                              alt="art_gallery_img"
                            />
                          </div>
                          <div
                            className="art_gallery_item position-relative overflow-hidden"
                            id="art_gallery_item_6"
                          >
                            <img
                              src="/assets/images/art_gallery_img_6.png"
                              alt="art_gallery_img"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about_section">
        <div className="about_sec">
          <div className="container">
            <div className="about_area">
              <div className="row align-items-center">
                <div className="col-xxl-6">
                  <div className="about_content d-flex flex-column font_two">
                    <h2 className="font_one heading_text_2">
                      About Digi Cartt
                    </h2>
                    <div className="about_info d-flex flex-column">
                      <p className="d-sm-block" style={{ textAlign: "justify" }}>
                        Digi Cartt is a curated digital art marketplace for
                        modern creatives, offering premium downloads and
                        gallery‑grade prints across styles like surreal
                        double‑exposures, abstracts, portraits, and limited
                        editions, all delivered instantly after checkout with
                        clear, flexible licensing. It focuses on print‑ready
                        quality, consistent color profiles, and detailed
                        size/DPI guides so every piece looks outstanding on
                        screen or on wall.
                      </p>
                      <p className="d-sm-block d-none" style={{ textAlign: "justify" }}>
                        Built by designers for creators and collectors, Digi
                        Cartt pairs editorial curation with a smooth buying
                        experience—secure payments, lifetime access to past
                        purchases in the account, and optional museum‑quality
                        giclée prints on select works. Artists benefit from a
                        transparent creator program, portfolio curation,
                        analytics, and IP protection that keeps buyer files
                        clean while safeguarding original work.
                      </p>
                    </div>
                    <div className="about_btn_area">
                      <a href="/about" className="about_btn arrow_btn_three">
                        <span className="btn_text">Explore More</span>
                        <span className="btn_icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M12.9998 1H13.7498V0.25H12.9998V1ZM12.9998 1L12.4695 0.469675L0.469665 12.4697L1 13L1.53033 13.5303L13.5301 1.53033L12.9998 1ZM5.99991 1V1.75H12.9998V1V0.25H5.99991V1ZM12.9998 1H12.2498V7.99994H12.9998H13.7498V1H12.9998Z"
                              fill="#E7D5AD"
                            />
                          </svg>
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="col-xxl-6">
                  <div className="about_img_aera position-relative overflow-hidden">
                    <div className="about_img">
                      <img
                        src="/assets/images/home_about_img_1.png"
                        alt="about_img"
                      />
                    </div>
                    <div className="about_img">
                      <img
                        src="/assets/images/home_about_img_2.png"
                        alt="about_img"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digi Packs Section */}
      

      {/* Contact Section */}
      {/* <section className="contact_section">
        <div className="contact_sec">
          <div className="container">
            <div className="contact_area color_lightwhite">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <div className="contact_area_info d-flex flex-column">
                    <div className="contact_info_title_area d-flex flex-column text-lg-start text-center justify-content-lg-start justify-content-center">
                      <h2 className="heading_text_2">
                        Get the latest Digi Cart updates
                      </h2>
                      <p className="font_two">
                        Subscribe for product releases, marketplace tips, and
                        exclusive offers—straight to the inbox.
                      </p>
                    </div>
                    <form action="#" className="contact_area_form">
                      <div className="contact_area_form_box position-relative">
                        <input
                          type="email"
                          placeholder="Enter your email"
                          required
                        />
                        <button type="submit" className="blue_btn">
                          Submit Email
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="col-lg-6 d-lg-flex d-none">
                  <div className="contact_img_area_info">
                    <div className="contact_img_area">
                      <img
                        src="/assets/images/contact_sec_img_1.png"
                        alt="contact_sec_img"
                      />
                      <img
                        src="/assets/images/contact_sec_img_2.png"
                        alt="contact_sec_img"
                      />
                    </div>
                    <div className="contact_img_area">
                      <img
                        src="/assets/images/contact_sec_img_3.png"
                        alt="contact_sec_img"
                      />
                      <img
                        src="/assets/images/contact_sec_img_4.png"
                        alt="contact_sec_img"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Home;
